// ═══════════════════════════════════════════════════════════════
// MainStreet — Organizations List
// Search, filter, paginated table of all organizations
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Select, Stack, Modal, PasswordInput,
  Pagination,
} from '../../components/gab/GabComponents';
import { IconSearch, IconPlus, IconRefresh, IconAlertCircle, IconCheck } from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { useMainStreetStore } from '../../store/mainstreetStore';
import { useRoleStore } from '../../store/roleStore';
import { createIgnatiusUser } from '../../lib/api-switch';
import { SECTOR_TYPES, OP_STATUS } from './constants';
import { useModulePermission } from '../../hooks/useModulePermission';
import { useIncidentZones } from '../../hooks/useIncidentZones';
import { isInsideZone } from '../../lib/geoopsGeo';
import { formatPhone } from '../../lib/phoneFormat';

const PAGE_SIZE = 25;

const SECTOR_SELECT = [
  { value: 'all', label: 'All Sectors' },
  ...SECTOR_TYPES.map(s => ({ value: s.id, label: s.label })),
];

const STATUS_SELECT = [
  { value: 'all', label: 'All Statuses' },
  ...OP_STATUS.map(s => ({ value: s.id, label: s.label })),
];

function sectorLabel(id: string): string {
  return SECTOR_TYPES.find(s => s.id === id)?.label ?? id;
}

function opStatusEntry(id: string) {
  return OP_STATUS.find(s => s.id === id);
}

export default function Organizations() {
  const navigate = useNavigate();
  const { canEdit } = useModulePermission('mainstreet');
  const { organizations, loadingOrgs, loadOrganizations, updateOrganization } = useMainStreetStore();
  const { assignRole } = useRoleStore();

  const pendingOrgs = useMemo(() => organizations.filter(o => o.opStatus === 'pending'), [organizations]);

  // Zone awareness
  const { zones: incidentZones } = useIncidentZones();
  const activeZones = useMemo(() => incidentZones.filter(z => z.status === 'active'), [incidentZones]);
  const orgZoneMap = useMemo(() => {
    if (activeZones.length === 0) return new Set<number>();
    const inZone = new Set<number>();
    organizations.forEach(o => {
      if (!o.coordinates) return;
      const parts = o.coordinates.split(',').map(s => parseFloat(s.trim()));
      if (parts.length < 2 || isNaN(parts[0]) || isNaN(parts[1])) return;
      if (activeZones.some(z => z.geometry && isInsideZone(parts[0], parts[1], z.geometry as any))) {
        inZone.add(o.id);
      }
    });
    return inZone;
  }, [organizations, activeZones]);

  // Approve modal state
  const [approveTarget, setApproveTarget] = useState<typeof organizations[0] | null>(null);
  const [approvePw, setApprovePw] = useState('');
  const [approving, setApproving] = useState(false);

  const handleApprove = async () => {
    if (!approveTarget) return;
    const org = approveTarget;
    setApproving(true);
    try {
      // Parse notes for contact info
      let meta: Record<string, string> = {};
      try { meta = JSON.parse(org.notes || '{}'); } catch { /* */ }
      const email = org.email || meta.contactEmail || '';
      const contactName = org.primaryContact || meta.contactName || org.name;
      const nameParts = contactName.split(' ');
      const firstName = nameParts[0] || contactName;
      const lastName = nameParts.slice(1).join(' ') || '';
      const tempPw = approvePw || `Welcome${Math.random().toString(36).slice(2, 8)}!`;

      // 1. Create Ignatius user account
      if (email) {
        try {
          await createIgnatiusUser({
            firstName,
            lastName,
            email,
            role: 'User',
          } as any);
        } catch (err) {
          // User may already exist — continue with role assignment
          console.warn('Create user:', err);
        }

        // 2. Assign biz_owner role
        await assignRole(email, contactName, '', 'biz_owner');
      }

      // 3. Update org status
      await updateOrganization(org.id, { opStatus: 'operational' });

      notifications.show({
        title: 'Approved',
        message: email
          ? `${org.name} approved. Login created for ${email} with password: ${tempPw}`
          : `${org.name} approved. No email — create login manually.`,
        color: 'green',
        autoClose: 15000,
      });
      setApproveTarget(null);
      setApprovePw('');
      await loadOrganizations();
    } catch (err) {
      notifications.show({ title: 'Error', message: err instanceof Error ? err.message : 'Failed to approve', color: 'red' });
    } finally {
      setApproving(false);
    }
  };

  const [search, setSearch] = useState('');
  const [sectorFilter, setSectorFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [listFilter, setListFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState<string>('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const toggleSort = (field: string) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };
  const sortIcon = (f: string) => sortField === f ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  const { orgLists, loadOrgLists } = useMainStreetStore();
  useEffect(() => { loadOrganizations(); loadOrgLists(); }, []);

  const listFilterSelect = useMemo(() => [
    { value: 'all', label: 'All Lists' },
    ...orgLists.map(l => ({ value: String(l.id), label: `${l.name} (${l.orgIds.split(',').filter(Boolean).length})` })),
  ], [orgLists]);

  const listOrgIds = useMemo(() => {
    if (listFilter === 'all') return null;
    const list = orgLists.find(l => String(l.id) === listFilter);
    return list ? new Set(list.orgIds.split(',').filter(Boolean).map(Number)) : null;
  }, [listFilter, orgLists]);

  const filtered = useMemo(() => {
    let list = organizations;
    if (listOrgIds) list = list.filter(o => listOrgIds.has(o.id));
    if (sectorFilter !== 'all') list = list.filter(o => o.sectorType === sectorFilter);
    if (statusFilter !== 'all') list = list.filter(o => o.opStatus === statusFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(o =>
        (o.name || '').toLowerCase().includes(q) ||
        (o.city || '').toLowerCase().includes(q) ||
        (o.primaryContact || '').toLowerCase().includes(q) ||
        (o.email || '').toLowerCase().includes(q),
      );
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      if (!sortField) return (a.name || '').localeCompare(b.name || '');
      const va = (a as any)[sortField] ?? '';
      const vb = (b as any)[sortField] ?? '';
      const numA = Number(va); const numB = Number(vb);
      if (!isNaN(numA) && !isNaN(numB) && va !== '' && vb !== '') return (numA - numB) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [organizations, sectorFilter, statusFilter, search, listOrgIds, sortField, sortDir]);

  useEffect(() => { setPage(1); }, [sectorFilter, statusFilter, search, listFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Organizations</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
            loading={loadingOrgs} onClick={() => loadOrganizations()}>Refresh</Button>
          {canEdit && <Button size="xs" leftSection={<IconPlus size={14} />}
            onClick={() => navigate('/mainstreet/org/new')}>Add Organization</Button>}
        </Group>
      </Group>

      {/* Pending Registrations Banner */}
      {canEdit && pendingOrgs.length > 0 && (
        <Paper p="md" mb="md" radius="sm" withBorder
          style={{ borderLeft: '4px solid var(--mantine-color-violet-5)', background: 'var(--mantine-color-violet-0)' }}>
          <Group justify="space-between" mb="xs">
            <Group gap="xs">
              <IconAlertCircle size={16} color="var(--mantine-color-violet-6)" />
              <Text fw={600} size="sm" c="violet.8">
                {pendingOrgs.length} Pending Registration{pendingOrgs.length > 1 ? 's' : ''}
              </Text>
            </Group>
            <Button size="compact-xs" variant="subtle" color="violet"
              onClick={() => setStatusFilter('pending')}>
              View All
            </Button>
          </Group>
          {pendingOrgs.slice(0, 5).map(org => {
            let meta: Record<string, string> = {};
            try { meta = JSON.parse(org.notes || '{}'); } catch { /* */ }
            return (
              <Group key={org.id} justify="space-between" py={4}
                style={{ borderBottom: '1px solid var(--mantine-color-violet-1)' }}>
                <div>
                  <Text size="sm" fw={500}>{org.name}</Text>
                  <Text size="xs" c="dimmed">
                    {org.primaryContact} &middot; {org.email} &middot; {org.sectorType}
                    {meta.registeredAt && ` &middot; ${new Date(meta.registeredAt).toLocaleDateString()}`}
                  </Text>
                </div>
                <Group gap="xs">
                  <Button size="compact-xs" color="green" variant="light"
                    leftSection={<IconCheck size={12} />}
                    onClick={() => { setApproveTarget(org); setApprovePw(''); }}>
                    Approve
                  </Button>
                  <Button size="compact-xs" variant="subtle"
                    onClick={() => navigate(`/mainstreet/org/${org.id}`)}>
                    Review
                  </Button>
                </Group>
              </Group>
            );
          })}
        </Paper>
      )}

      <Group mb="md" gap="sm">
        <TextInput placeholder="Search name, city, contact, email..."
          leftSection={<IconSearch size={14} />} value={search}
          onChange={e => setSearch(e.target.value)} size="xs"
          style={{ flex: 1, minWidth: 200 }} />
        <Select data={SECTOR_SELECT} value={sectorFilter}
          onChange={v => setSectorFilter(v ?? 'all')} size="xs" w={200} />
        <Select data={STATUS_SELECT} value={statusFilter}
          onChange={v => setStatusFilter(v ?? 'all')} size="xs" w={180} />
        {orgLists.length > 0 && (
          <Select data={listFilterSelect} value={listFilter}
            onChange={v => setListFilter(v ?? 'all')} size="xs" w={200} />
        )}
      </Group>

      <Text size="xs" c="dimmed" mb="xs">{filtered.length} organization{filtered.length !== 1 ? 's' : ''}</Text>

      <Paper withBorder>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--mantine-color-gray-3)' }}>
                <th style={{ padding: '8px 12px', textAlign: 'left', cursor: 'pointer' }} onClick={() => toggleSort('name')}>Name{sortIcon('name')}</th>
                <th style={{ padding: '8px 12px', textAlign: 'left', cursor: 'pointer' }} onClick={() => toggleSort('sectorType')}>Sector{sortIcon('sectorType')}</th>
                <th style={{ padding: '8px 12px', textAlign: 'left', cursor: 'pointer' }} onClick={() => toggleSort('city')}>City / State{sortIcon('city')}</th>
                <th style={{ padding: '8px 12px', textAlign: 'left', cursor: 'pointer' }} onClick={() => toggleSort('opStatus')}>Status{sortIcon('opStatus')}</th>
                {activeZones.length > 0 && <th style={{ padding: '8px 12px', textAlign: 'left' }}>Zone</th>}
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Phone</th>
                <th style={{ padding: '8px 12px', textAlign: 'left', cursor: 'pointer' }} onClick={() => toggleSort('lastVerified')}>Last Verified{sortIcon('lastVerified')}</th>
              </tr>
            </thead>
            <tbody>
              {paged.length === 0 && (
                <tr>
                  <td colSpan={activeZones.length > 0 ? 7 : 6} style={{ padding: 24, textAlign: 'center', color: '#888' }}>
                    {loadingOrgs ? 'Loading...' : 'No organizations found'}
                  </td>
                </tr>
              )}
              {paged.map(org => {
                const status = opStatusEntry(org.opStatus);
                return (
                  <tr key={org.id}
                    onClick={() => navigate(`/mainstreet/org/${org.id}`)}
                    style={{ borderBottom: '1px solid var(--mantine-color-gray-2)', cursor: 'pointer' }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'var(--mantine-color-gray-0)')}
                    onMouseLeave={e => (e.currentTarget.style.background = '')}>
                    <td style={{ padding: '8px 12px', fontWeight: 500 }}>{org.name || '(unnamed)'}</td>
                    <td style={{ padding: '8px 12px' }}>
                      {org.sectorType && <Badge size="xs" variant="light">{sectorLabel(org.sectorType)}</Badge>}
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      {[org.city, org.state].filter(Boolean).join(', ') || '-'}
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      {status
                        ? <Badge size="xs" color={status.color}>{status.label}</Badge>
                        : <Text size="xs" c="dimmed">-</Text>}
                    </td>
                    {activeZones.length > 0 && (
                      <td style={{ padding: '8px 12px' }}>
                        {orgZoneMap.has(org.id)
                          ? <Badge size="xs" color="red" variant="light">In Zone</Badge>
                          : <Text size="xs" c="dimmed">—</Text>}
                      </td>
                    )}
                    <td style={{ padding: '8px 12px' }}>{org.phone ? formatPhone(org.phone) : '-'}</td>
                    <td style={{ padding: '8px 12px' }}>
                      {org.lastVerified ? new Date(org.lastVerified).toLocaleDateString() : '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Paper>

      {totalPages > 1 && (
        <Group justify="center" mt="md">
          <Pagination total={totalPages} value={page} onChange={setPage} size="sm" />
        </Group>
      )}

      {/* Approve Business Modal */}
      <Modal opened={approveTarget !== null} onClose={() => setApproveTarget(null)}
        title="Approve Business Registration" size="md">
        {approveTarget && (
          <Stack>
            <Paper p="md" bg="gray.0" radius="sm" withBorder>
              <Text fw={600} size="sm">{approveTarget.name}</Text>
              <Text size="xs" c="dimmed">{approveTarget.primaryContact} &middot; {approveTarget.email}</Text>
              <Text size="xs" c="dimmed">{approveTarget.sectorType} &middot; {[approveTarget.address, approveTarget.city, approveTarget.state].filter(Boolean).join(', ')}</Text>
            </Paper>
            <Text size="sm">
              This will create an Ignatius login for <strong>{approveTarget.email}</strong> and assign the
              Business Owner role. Set a temporary password below.
            </Text>
            <PasswordInput
              label="Temporary Password"
              value={approvePw}
              onChange={e => setApprovePw(e.target.value)}
              placeholder="Leave blank for auto-generated"
              description="The business owner should change this on first login"
            />
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setApproveTarget(null)}>Cancel</Button>
              <Button color="green" loading={approving} onClick={handleApprove}
                leftSection={<IconCheck size={14} />}>
                Approve & Create Login
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </div>
  );
}
